/**
 * Firebase web initialization using firebase-js-sdk
 * Used when Platform.OS === 'web'
 * Config injected by webpack DefinePlugin (FIREBASE_WEB_CONFIG)
 */

import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword as fbSignIn,
  createUserWithEmailAndPassword as fbCreateUser,
  signInAnonymously as fbSignInAnon,
  signOut as fbSignOut,
  type Auth,
} from 'firebase/auth';
import { getFirestore, enableIndexedDbPersistence } from 'firebase/firestore';
import { getAnalytics } from 'firebase/analytics';

declare const FIREBASE_WEB_CONFIG: {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
};

const firebaseConfig = typeof FIREBASE_WEB_CONFIG !== 'undefined' ? FIREBASE_WEB_CONFIG : {
  apiKey: '',
  authDomain: '',
  projectId: '',
  storageBucket: '',
  messagingSenderId: '',
  appId: '',
};

let _auth: Auth | null = null;
let _firestore: ReturnType<typeof getFirestore> | null = null;
let _analytics: ReturnType<typeof getAnalytics> | null = null;

function initFirebaseWeb() {
  if (_auth) return { auth: _auth, firestore: _firestore, analytics: _analytics };
  if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
    return { auth: null, firestore: null, analytics: null };
  }
  try {
    const app = initializeApp(firebaseConfig);
    const authInstance = getAuth(app);
    _auth = authInstance;
    _firestore = getFirestore(app);
    _analytics = getAnalytics(app);
    enableIndexedDbPersistence(_firestore).catch(() => {});
    return { auth: _auth, firestore: _firestore, analytics: _analytics };
  } catch {
    return { auth: null, firestore: null, analytics: null };
  }
}

const { auth: authInstance, firestore, analytics } = initFirebaseWeb();

// Adapter: firebase-js-sdk uses functional API, authService expects method API
const auth = authInstance
  ? {
      get currentUser() {
        return authInstance.currentUser;
      },
      signInWithEmailAndPassword: (email: string, password: string) =>
        fbSignIn(authInstance, email, password),
      createUserWithEmailAndPassword: (email: string, password: string) =>
        fbCreateUser(authInstance, email, password),
      signInAnonymously: () => fbSignInAnon(authInstance),
      signOut: () => fbSignOut(authInstance),
    }
  : null;

export { auth, analytics };
export const db = firestore;
export const COLLECTIONS = {
  USERS: 'users',
  DOSE_LOGS: 'dose_logs',
  FLIGHTS: 'flights',
  ALERTS: 'alerts',
} as const;
export const isFirebaseAvailable = auth !== null && db !== null;
